import React, { useEffect, useState } from 'react'
import './Toast.css'

export type ToastType = 'success' | 'error' | 'info'

export interface ToastMessage {
  id: string
  message: string
  type: ToastType
  duration?: number
}

interface ToastProps {
  message: string
  type?: ToastType
  duration?: number
  onClose?: () => void
}

/**
 * Toast Component - Individual toast notification
 *
 * Displays a temporary notification message with automatic dismissal.
 *
 * Features:
 * - Auto-dismiss after duration (default 3s)
 * - Type-based styling (success, error, info)
 * - Slide-in animation
 * - Accessible (ARIA live region, role="status")
 *
 * @component
 */
export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 3000,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false)
      setTimeout(() => {
        onClose?.()
      }, 300) // Wait for fade-out animation
    }, duration)

    return () => clearTimeout(timer)
  }, [duration, onClose])

  return (
    <div
      className={`toast toast--${type} ${!isVisible ? 'toast--hidden' : ''}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="toast__icon">{getIcon(type)}</div>
      <div className="toast__message">{message}</div>
    </div>
  )
}

/**
 * Get icon for toast type
 */
function getIcon(type: ToastType): string {
  switch (type) {
    case 'success':
      return '✓'
    case 'error':
      return '✕'
    case 'info':
      return 'ⓘ'
    default:
      return 'ⓘ'
  }
}

/**
 * ToastContainer Component - Manages multiple toast notifications
 *
 * Stacks toast notifications in bottom-center position.
 * Automatically manages toast lifecycle (add, remove).
 *
 * @component
 */
interface ToastContainerProps {
  toasts: ToastMessage[]
  onRemoveToast: (id: string) => void
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onRemoveToast,
}) => {
  if (toasts.length === 0) {
    return null
  }

  return (
    <div className="toast-container" aria-label="Notifications">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => onRemoveToast(toast.id)}
        />
      ))}
    </div>
  )
}
